import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import {
  createDrivePreviewUrl,
  fetchDriveDocxHtml,
  fetchDriveFileText,
  formatBytes,
  type DriveFile,
} from '../api/fileDrive';
import { guessDelimiter, parseDelimited } from '../utils/parseDelimited';
import './DriveFilePreview.css';

/** Rows past this are left out; the point is a look at the file, not a data grid. */
const MAX_CSV_ROWS = 500;

interface Props {
  file: DriveFile;
  onClose: () => void;
  onDownload: (file: DriveFile) => void;
  /** Surfaces a session expiry the same way the rest of the page does. */
  onError: (err: unknown, fallback: string) => string;
}

type Loaded =
  | { kind: 'media'; url: string }
  | { kind: 'text'; text: string; truncated: boolean }
  | { kind: 'csv'; rows: string[][]; truncated: boolean }
  | { kind: 'docx'; html: string };

const DriveFilePreview: React.FC<Props> = ({ file, onClose, onDownload, onError }) => {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setError(null);

    const load = async () => {
      try {
        if (file.previewKind === 'text') {
          const data = await fetchDriveFileText(file.id);
          if (!cancelled) setLoaded({ kind: 'text', text: data.text, truncated: data.truncated });
          return;
        }
        if (file.previewKind === 'csv') {
          const data = await fetchDriveFileText(file.id);
          const all = parseDelimited(data.text, guessDelimiter(data.text));
          if (!cancelled) {
            setLoaded({
              kind: 'csv',
              rows: all.slice(0, MAX_CSV_ROWS),
              truncated: data.truncated || all.length > MAX_CSV_ROWS,
            });
          }
          return;
        }
        if (file.previewKind === 'docx') {
          const data = await fetchDriveDocxHtml(file.id);
          if (!cancelled) setLoaded({ kind: 'docx', html: data.html });
          return;
        }
        const { url } = await createDrivePreviewUrl(file.id);
        if (!cancelled) setLoaded({ kind: 'media', url });
      } catch (err) {
        const message = onError(err, 'Failed to open this file.');
        if (!cancelled) setError(message);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [file.id, file.previewKind, onError]);

  const body = useMemo(() => {
    if (error) {
      return (
        <div className="drive-preview-state drive-preview-state--error">
          <p>{error}</p>
          <button type="button" className="drive-button" onClick={() => onDownload(file)}>
            Download instead
          </button>
        </div>
      );
    }
    if (!loaded) {
      return <div className="drive-preview-state">Opening {file.name}…</div>;
    }

    if (loaded.kind === 'media') {
      if (file.previewKind === 'image') {
        return (
          <div className="drive-preview-canvas drive-preview-canvas--dark">
            <img className="drive-preview-image" src={loaded.url} alt={file.name} />
          </div>
        );
      }
      if (file.previewKind === 'video') {
        return (
          <div className="drive-preview-canvas drive-preview-canvas--dark">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video className="drive-preview-video" src={loaded.url} controls autoPlay={false} preload="metadata" />
          </div>
        );
      }
      if (file.previewKind === 'audio') {
        return (
          <div className="drive-preview-canvas">
            <audio className="drive-preview-audio" src={loaded.url} controls preload="metadata" />
          </div>
        );
      }
      return (
        <iframe className="drive-preview-frame" src={loaded.url} title={`${file.name} preview`} />
      );
    }

    if (loaded.kind === 'text') {
      return (
        <div className="drive-preview-scroll">
          {loaded.truncated && (
            <p className="drive-preview-note">
              Showing the beginning of this file. Download it to read the rest.
            </p>
          )}
          <pre className="drive-preview-text">{loaded.text}</pre>
        </div>
      );
    }

    if (loaded.kind === 'csv') {
      const [header, ...rows] = loaded.rows;
      return (
        <div className="drive-preview-scroll">
          {loaded.truncated && (
            <p className="drive-preview-note">
              Showing the first {rows.length} rows. Download the file for the full data.
            </p>
          )}
          <table className="drive-preview-table">
            {header && (
              <thead>
                <tr>
                  {header.map((cell, i) => (
                    <th key={i}>{cell}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="drive-preview-scroll">
        {/* mammoth builds this markup from the document's own structure on the
            server, so there is no author-supplied HTML passing through. */}
        <div className="drive-preview-document" dangerouslySetInnerHTML={{ __html: loaded.html }} />
      </div>
    );
  }, [error, loaded, file, onDownload]);

  return (
    <Modal isOpen onClose={onClose} title={file.name} fullScreen contentClassName="drive-preview-modal">
      <div className="drive-preview-body">{body}</div>
      <div className="drive-preview-footer">
        <span className="drive-preview-meta">{formatBytes(file.sizeBytes)}</span>
        <div className="drive-preview-footer-actions">
          <button type="button" className="drive-button" onClick={() => onDownload(file)}>
            Download
          </button>
          <button type="button" className="drive-button drive-button--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DriveFilePreview;
